// @ts-nocheck

import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import some from 'lodash/some';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { yieldCallback } from '@zillow/yield-callback';

import CarouselV3 from 'app/shared/modules/CarouselV3';
import Linker from 'app/shared/modules/Linker';
import { ListingProvider } from 'app/shared/modules/listing-cards/ListingCardProvider';
import ListingCardV3 from 'app/shared/modules/listing-cards/ListingCardV3';
import Text from 'app/shared/core/Text';
import Button from 'app/shared/core/Button';
import ImpressionTracking from 'app/shared/modules/ImpressionTracking';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackImpressionForPropertyCardEvent,
} from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';
import {
  homeHubSectionTitleContainerCSS,
  listingCollectionHeaderTextCSS,
  resumeSearchButtonContainerCSS,
  resumeSearchContainerCSS,
  resumeSearchTextCSS,
} from 'app/shared/modules/HomeHub/styles';

const ClickstreamLocationMap = {
  savedSearch: 'saved_search_collection',
  similarToFavorite: 'similar_to_favorite_collection',
  similarToContacted: 'similar_to_contacted_collection',
  recentSearch: 'recent_search_collection',
  luxuryApartments: 'luxury_apartments_collection',
  affordableApartments: 'affordable_apartments_collection',
  promoApartments: 'promo_apartments_collection',
  popularApartments: 'popular_apartments_collection',
  recommendedSearch: 'recommended_search_collection',
  acceptsApplication: 'accepts_application_collection',
};

const CARDS_ABOVE_THE_FOLD = {
  mobile: 2, // 1.5 cards visible, load 2 to be safe
  tablet: 4, // 3-4 cards visible
  desktop: 4, // 3-4 cards visible
};

/**
 * generic class for collections of listings rendered inside a carousel
 */
class ListingCollection extends Component {
  static propTypes = {
    listings: PropTypes.object,
    title: PropTypes.string,
    collectionIndex: PropTypes.number,
  };

  trackImpression = (listing) => {
    const { collectionType } = this.props;
    const triggerLocation = ClickstreamLocationMap[collectionType];
    emitTrackImpressionForPropertyCardEvent({ listing, triggerLocation });
  };

  trackClick = (listing) => {
    const { collectionType } = this.props;
    const triggerLocation = ClickstreamLocationMap[collectionType];
    emitTrackClicksIntoPropertyEvent({ listing, triggerLocation });
  };

  yieldHandleTransitionTo = yieldCallback(() => {
    const { url } = this.props;
    window.router.transitionTo(url);
  });

  handleSeeAllOrResumeClick = (e) => {
    // Prevent the browser from going through with its default <a> behavior
    e.preventDefault();

    this.yieldHandleTransitionTo();
  };

  render() {
    const {
      title,
      listings,
      filter,
      url,
      collectionType,
      numOfNewResults,
      filterSummary,
      name,
      isMobile,
      isTablet,
      collectionIndex = 0,
    } = this.props;
    const titleId = title.toLowerCase().replaceAll(' ', '-');

    const collectionTypeTest = 'notSavedSearch';

    if (isEmpty(listings)) {
      return null;
    }

    const listingsArray = Object.values(listings);
    const isShowingTotalPrice = some(listingsArray, (listing) => shouldShowTotalMonthlyPriceBadge(listing));

    // Calculate how many cards are above the fold based on device type
    const aboveFoldCount = isMobile
      ? CARDS_ABOVE_THE_FOLD.mobile
      : isTablet
        ? CARDS_ABOVE_THE_FOLD.tablet
        : CARDS_ABOVE_THE_FOLD.desktop;

    // Determine if this collection should apply eager loading
    const shouldApplyEagerLoading = isMobile ? collectionIndex === 0 : collectionIndex < 2;

    const savedSearchHeader = (
      <div className={homeHubSectionTitleContainerCSS}>
        {collectionTypeTest === 'savedSearch' && (
          <div>
            <h3 className={listingCollectionHeaderTextCSS}>Matching your saved search: {name}</h3>
            <div className={resumeSearchContainerCSS}>
              <div className={resumeSearchTextCSS} size="sm">
                {filterSummary ? `${filterSummary} | ` : null}
                {numOfNewResults} new listings
              </div>
              <div className={resumeSearchButtonContainerCSS}>
                <Button size="sm" btnType="primary" onClick={this.handleSeeAllOrResumeClick}>
                  Resume this search
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );

    return (
      <CarouselV3
        aria-label={title}
        freeScrollMode={isMobile}
        header={collectionTypeTest !== 'savedSearch' ? title : savedSearchHeader}
        headerTag={collectionTypeTest !== 'savedSearch' ? 'h3' : undefined}
        itemsPerPage={isMobile ? 1 : 3}
        showPaginationDots
        seeAllButton={
          url && collectionTypeTest !== 'savedSearch' ? (
            <Linker to={url} aria-describedby={titleId} onClick={this.handleSeeAllOrResumeClick}>
              <Text size="sm">See all</Text>
            </Linker>
          ) : undefined
        }
        style={{ marginBottom: '24px', paddingLeft: '8px', paddingRight: '8px' }}
      >
        {map(Object.values(listings), (listingData, index) => {
          return (
            <ListingProvider
              listing={listingData}
              triggerLocation="my_hub_page"
              triggerObject="property_card"
              key={`${listingData.aliasEncoded}-container`}
            >
              <>
                <ListingCardV3
                  isCarouselCard
                  onClick={() => this.trackClick(listingData)}
                  shouldLazyLoadListingCardPhotos={shouldApplyEagerLoading ? index >= aboveFoldCount : true}
                  highPriority={index === 0 && collectionIndex === 0}
                  forceUniformHeight={isShowingTotalPrice}
                />
                <ImpressionTracking analyticsCallback={() => this.trackImpression(listingData)} />
              </>
            </ListingProvider>
          );
        })}
      </CarouselV3>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    isMobile: state.app.device.screenWidth === 'sm' || state.app.device.screenWidth === 'xs',
    isTablet: state.app.device.screenWidth === 'md',
  };
};

export default connect(mapStateToProps)(ListingCollection);
